/**
 * Single source of truth for the viewer's atmosphere colours (issue #60):
 * sky horizon/zenith, distance fog, and the sun/ambient light tint all read
 * from this one palette, so haze and sunlight can never drift out of sync.
 * Pure RGB maths, three.js-free (mirrors colorGrade.ts) — sky.ts, fade.ts,
 * and lighting.ts each convert a colour to a three.js `Color`/hex at their
 * own three.js boundary.
 *
 * Ray II's viewer has one fixed time-of-day (no day/night cycle), so a
 * single authored palette is enough. See the mining report
 * (outputs/qa/mining/05_visual_quality.md §4) for the reference project's
 * full Hillaire/Bruneton precomputed-atmosphere approach, deliberately not
 * ported here — this fixed-time-of-day use case doesn't need it.
 */
import type { RGB } from './colorGrade';

export interface AtmospherePalette {
  /** Sky colour at the horizon; also the distance-fog colour, so fogged
   * terrain fades seamlessly into the sky instead of a hard seam. */
  horizon: RGB;
  /** Sky colour overhead. */
  zenith: RGB;
  /** Directional sun light colour/tint. */
  sun: RGB;
  /** Ambient fill light colour (sky-bounce tint). */
  ambient: RGB;
}

/** Authored once for the fixed time-of-day. `horizon`/`sun`/`ambient` match
 * the flat colours the viewer used pre-#60 (0xb9c7cf background/fog,
 * 0xfff2df sun, 0x8fa6bf ambient) so this change adds a sky gradient and
 * fog without shifting the existing look's colour identity; `zenith` is new. */
export const ATMOSPHERE_PALETTE: AtmospherePalette = {
  horizon: { r: 0.725, g: 0.78, b: 0.812 },
  zenith: { r: 0.25, g: 0.42, b: 0.62 },
  sun: { r: 1, g: 0.949, b: 0.875 },
  ambient: { r: 0.561, g: 0.651, b: 0.749 },
};

/** Converts a 0..1 RGB triple to a 24-bit hex integer (three.js Color's
 * constructor format), clamping any out-of-range channel. */
export function rgbToHex(c: RGB): number {
  const toByte = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return (toByte(c.r) << 16) | (toByte(c.g) << 8) | toByte(c.b);
}
